import {
  and,
  collection,
  doc,
  documentId,
  limit,
  query,
  runTransaction,
  serverTimestamp,
  startAfter,
  where,
  type CollectionReference,
  type DocumentData,
  type DocumentReference,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { StreamInterface } from 'tricklejs/dist/types'
import { ZodError } from 'zod'
import {
  collectionSnapshots,
  convertDocumentSnapshotToModel,
  modelItemStream,
} from '../../firestore-mobx/stream'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { UserProfile } from '../../models/UserProfile'
import { unsupportedConverter } from '../shared/noops'
import type { FirestoreUserProfile } from './schema'
import type { UserProfileRole } from './types'
import { schema } from './schema'
import { partition, unique } from '../../util/arrays'
import {
  deleteDocWithError,
  getDocsWithError,
  getDocWithError,
  setDocWithError,
  updateDocWithError,
} from '../../firestore-mobx/fetch'

export * from './schema'

const converter: FirestoreDataConverter<FirestoreUserProfile> = {
  toFirestore: unsupportedConverter,
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    try {
      return schema.parse(data)
    } catch (e) {
      if (e instanceof ZodError) {
        console.error('Error parsing user profile', e.errors)
      }
      throw e
    }
  },
}

export const getColRef = (
  firestore: Firestore
): CollectionReference<FirestoreUserProfile, DocumentData> => {
  return collection(firestore, 'user_profile').withConverter(converter)
}

const docRef = (
  firestore: Firestore,
  userId: string
): DocumentReference<FirestoreUserProfile, DocumentData> => {
  return doc(getColRef(firestore), userId)
}

export const getUserProfile = (
  repository: FirebaseRepository,
  params: { userId: string }
): StreamInterface<UserProfile> => {
  const ref = docRef(repository.firestore, params.userId)

  return modelItemStream(repository, ref, UserProfile)
}

export const fetchUserProfile = async (
  repository: FirebaseRepository,
  params: { userId: string }
): Promise<UserProfile | null> => {
  const ref = docRef(repository.firestore, params.userId)
  const snapshot = await getDocWithError(ref, 'FetchUserProfileError')
  if (!snapshot.exists()) return null
  return convertDocumentSnapshotToModel(repository, snapshot, UserProfile)
}

export const getAssistedInstructorIDs = (
  repository: FirebaseRepository,
  userId: string
) => {
  const profileInstructorsCollectionRef = collection(
    repository.firestore,
    'user_profile',
    userId,
    'instructor'
  )
  return collectionSnapshots(profileInstructorsCollectionRef).map((snap) =>
    snap.docs.map((doc) => doc.id)
  )
}

export const fetchAssistedInstructors = async (
  repository: FirebaseRepository,
  userId?: string
) => {
  const profileInstructorsCollectionRef = collection(
    repository.firestore,
    'user_profile',
    userId ?? repository.currentUser?.uid ?? '',
    'instructor'
  )
  const snap = await getDocsWithError(
    profileInstructorsCollectionRef,
    'fetchAssistedInstructorsError'
  )
  return snap.docs.map((doc) => doc.id)
}

export const fetchUsersForAdmin = async (
  repository: FirebaseRepository,
  {
    role,
  }: {
    role: UserProfileRole | UserProfileRole[]
  }
) => {
  const roleArr = Array.isArray(role) ? role : [role]
  const colRef = getColRef(repository.firestore)
  const q = query(
    colRef,
    where('role', 'in', roleArr)
    // orderBy('lastName'),
    // limit(10)
  )

  const snap = await getDocsWithError(q, 'FetchUsersForAdminError')

  return snap.docs.map((doc) =>
    convertDocumentSnapshotToModel(repository, doc, UserProfile)
  )
}

export async function fetchAllUserProfiles(
  repository: FirebaseRepository,
  role: UserProfileRole | UserProfileRole[]
) {
  const roleArr = Array.isArray(role) ? role : [role]
  const users: UserProfile[] = []

  const documentLimit = 100
  let lastDocument: QueryDocumentSnapshot | undefined

  let work = true
  while (work) {
    let queryRef = query(
      getColRef(repository.firestore),
      where('role', 'in', roleArr),
      limit(documentLimit)
    )

    if (lastDocument) {
      queryRef = query(queryRef, startAfter(lastDocument))
    }

    const querySnapshot = await getDocsWithError(
      queryRef,
      'FetchAllUserProfilesError'
    )
    const docs = querySnapshot.docs

    if (docs.length === 0) {
      work = false
    } else {
      const converted = docs.map((doc) => {
        return convertDocumentSnapshotToModel(repository, doc, UserProfile)
      })
      users.push(...converted)
      lastDocument = querySnapshot.docs[querySnapshot.docs.length - 1]
    }
  }

  return users
}

export async function fetchAllUserProfilesByEmail(
  repository: FirebaseRepository,
  role: UserProfileRole | UserProfileRole[],
  emailSearch: string
) {
  const users: UserProfile[] = []
  const roleArr = Array.isArray(role) ? role : [role]

  const documentLimit = 100
  let lastDocument: QueryDocumentSnapshot | undefined

  let work = true
  while (work) {
    let queryRef = query(
      getColRef(repository.firestore),
      and(
        where('role', 'in', roleArr),
        where('emailAddress', '>=', emailSearch),
        where('emailAddress', '<', emailSearch + '\uf8ff')
      ),
      limit(documentLimit)
    )

    if (lastDocument) {
      queryRef = query(queryRef, startAfter(lastDocument))
    }

    const querySnapshot = await getDocsWithError(
      queryRef,
      'FetchAllUserProfilesError'
    )
    const docs = querySnapshot.docs

    if (docs.length === 0) {
      work = false
    } else {
      const converted = docs.map((doc) => {
        return convertDocumentSnapshotToModel(repository, doc, UserProfile)
      })
      users.push(...converted)
      lastDocument = querySnapshot.docs[querySnapshot.docs.length - 1]
    }
  }

  return users
}

export const fetchUserProfiles = async (
  repository: FirebaseRepository,
  params: { userIds: string[] }
): Promise<UserProfile[]> => {
  const userIds = unique(params.userIds).filter((id) => id)

  if (userIds.length === 0) return Promise.resolve([])

  const promises = userIds.map(async (userId) => {
    try {
      const ref = docRef(repository.firestore, userId)
      const snapshot = await getDocWithError(
        ref,
        'FetchUserProfileIndividualError'
      )
      return convertDocumentSnapshotToModel(repository, snapshot, UserProfile)
    } catch (error) {
      // If a user profile doesn't exist or we don't have access, skip it
      console.warn(`Failed to fetch user profile for ${userId}:`, error)
      return null
    }
  })

  const results = await Promise.all(promises)
  return results.filter((profile): profile is UserProfile => profile !== null)
}

/** Requires list permissions: */
export const fetchUserProfilesAsAdmin = (
  repository: FirebaseRepository,
  params: { userIds: string[] }
): Promise<UserProfile[]> => {
  const ref = getColRef(repository.firestore)
  // filter out empty strings
  const userIds = unique(params.userIds).filter((id) => id)

  if (userIds.length === 0) return Promise.resolve([])

  const limit = 30
  const promises: Promise<UserProfile[]>[] = []

  if (userIds.length > limit) {
    const partitions = partition(userIds, limit)

    partitions.forEach((part) => {
      const promise = new Promise<UserProfile[]>((resolve) => {
        const q = query(ref, where(documentId(), 'in', part))
        getDocsWithError(q, 'FetchUserProfilesPartitionError').then(
          (snapshot) => {
            const converted = snapshot.docs.map((doc) => {
              return convertDocumentSnapshotToModel(
                repository,
                doc,
                UserProfile
              )
            })

            resolve(converted)
          }
        )
      })
      promises.push(promise)
    })
  } else {
    const q = query(ref, where(documentId(), 'in', userIds))
    const promise = getDocsWithError(q, 'FetchUserProfilesError').then(
      (snapshot) => {
        return snapshot.docs.map((doc) => {
          return convertDocumentSnapshotToModel(repository, doc, UserProfile)
        })
      }
    )
    promises.push(promise)
  }

  return Promise.all(promises).then((results) => {
    return results.flat()
  })
}

export const addCatalogToUserProfile = (
  repository: FirebaseRepository,
  params: { catalogId: string; userId: string; organizationId?: string }
): Promise<void> => {
  const catalogCollectionRef = collection(
    repository.firestore,
    'user_profile',
    params.userId,
    'catalogs'
  )
  const docRef = doc(catalogCollectionRef, params.catalogId)

  return setDocWithError(docRef, {
    catalogId: params.catalogId,
    userId: params.userId,
    createdAt: serverTimestamp(),
    organizationId: params.organizationId,
  })
}

export const removeCatalogFromUserProfile = (
  repository: FirebaseRepository,
  params: { catalogId: string; userId: string }
): Promise<void> => {
  const catalogCollectionRef = collection(
    repository.firestore,
    'user_profile',
    params.userId,
    'catalogs'
  )
  const docRef = doc(catalogCollectionRef, params.catalogId)

  return deleteDocWithError(docRef, 'RemoveCatalogFromUserProfileError')
}

export const updateEmail = (
  repository: FirebaseRepository,
  userId: string,
  email: string
): Promise<void> => {
  const ref = docRef(repository.firestore, userId)

  return updateDocWithError(ref, { emailAddress: email }, 'updateEmail')
}

export const updateHideDefaultProducts = (
  repository: FirebaseRepository,
  userId: string,
  hideDefaultProducts: boolean
): Promise<void> => {
  const ref = docRef(repository.firestore, userId)

  return updateDocWithError(
    ref,
    { hideDefaultProducts, updatedAt: serverTimestamp() },
    'UpdateBillingPreferencesError'
  )
}

export const updateExternalCanDemo = (
  repository: FirebaseRepository,
  userId: string,
  externalCanDemo: boolean
): Promise<void> => {
  const ref = docRef(repository.firestore, userId)

  return updateDocWithError(
    ref,
    { externalCanDemo, updatedAt: serverTimestamp() },
    'UpdateExternalCanDemoError'
  )
}

export const updateUserIsTest = (
  repository: FirebaseRepository,
  userId: string,
  isTest: boolean
): Promise<void> => {
  const ref = docRef(repository.firestore, userId)

  return updateDocWithError(
    ref,
    { isTest, updatedAt: serverTimestamp() },
    'UpdateUserIsTestError'
  )
}

export const deleteAssistedInstructors = async (
  repository: FirebaseRepository,
  userId: string
): Promise<void> => {
  // populate with current instructor ids
  let instructorIds = new Set<string>(
    await fetchAssistedInstructors(repository, userId)
  )
  let attempts = 0

  while (instructorIds.size) {
    // sanity check to prevent forever loop
    if (attempts > 10)
      throw new Error(`Failed to delete all TA instructors for user ${userId}`)

    await runTransaction(repository.firestore, async (transaction) => {
      Array.from(instructorIds).forEach((instructorId) => {
        const ref = doc(
          repository.firestore,
          'user_profile',
          userId,
          'instructor',
          instructorId
        )
        transaction.delete(ref)
        instructorIds.delete(instructorId)
        attempts++
      })
    })

    // refetch to ensure none were added while we were deleting
    instructorIds = new Set<string>(
      await fetchAssistedInstructors(repository, userId)
    )
  }
}

export const fetchUserHideDefaultProducts = async (
  repository: FirebaseRepository,
  userId: string
): Promise<boolean> => {
  const ref = doc(getColRef(repository.firestore), userId)
  return getDocWithError(ref).then(
    (doc) => doc.data()?.hideDefaultProducts ?? false
  )
}
